// js/shared/privacy-summary.js — the privacy policy in four sentences, in both
// languages the app speaks, from ONE source.
//
// Queue item AQ. Settings › Privacy used to carry its own hand-written trust
// list — "encrypted in transit and at rest (AES-256)", "row-level security" —
// which is engineering vocabulary describing a SECURITY posture on a PRIVACY
// screen, and which had no relationship to privacy.html beyond both files
// happening to be about data. Two documents saying overlapping things in
// different words is how a policy and a product drift apart; the one that is
// legally operative is the policy, so the policy is the source.
//
// THE FOUR SENTENCES ARE PARAPHRASES OF NAMED SECTIONS, not new claims:
//
//   collect  → privacy.html §3  "Information we collect"
//   whoSees  → privacy.html §5  "When we share information"
//   keep     → privacy.html §9  "How long we keep information"
//   canDo    → privacy.html §10 "Your privacy rights"
//
// Each entry carries the section number it summarises so the reader can go
// read the operative text, and so a reviewer checking this file against the
// policy knows exactly which paragraph to check it against.
//
// BOTH SURFACES RENDER FROM HERE. privacy.html mounts them as its
// "In plain language" block; app.html's Privacy & Data tab renders the same
// four rows. scripts/meta/META-12-bilingual-parity.mjs compares the two packs
// key for key AND asserts that neither file carries a hard-coded copy of a
// sentence — the drift this module exists to prevent cannot come back by
// somebody pasting the English into the markup.
//
// FTC Act §5 (TRUST_COMPLIANCE_GUARDRAILS_2026_06_17.md): every sentence here
// must be true, substantiated, and honored. Nothing absolute ("100% secure"),
// nothing borrowed from a vendor's certifications, no HIPAA claim — KinetiqAF
// is a personal-training business and is not a HIPAA-covered entity.
//
// PURE. No DOM at module scope, no fetch, no cross-realm state — the same
// contract as parq-i18n.js / intake-i18n.js, which is what lets node read it.

// The order the rows render in. Keyed so a reorder is one edit, not two.
export const PRIVACY_SUMMARY_ORDER = Object.freeze(['collect', 'whoSees', 'keep', 'canDo']);

// Which privacy.html section each sentence summarises. Data, not prose, so the
// gate can assert the anchors still exist in the policy.
export const PRIVACY_SUMMARY_SECTIONS = Object.freeze({
  collect: { n: 3,  anchor: 'what' },
  whoSees: { n: 5,  anchor: 'share' },
  keep:    { n: 9,  anchor: 'retention' },
  canDo:   { n: 10, anchor: 'rights' },
});

const EN = {
  'sum.title': 'In plain language',
  'sum.intro': 'The four things people ask us most, answered here and written out in full in the policy.',
  'sum.collect.q': 'What we collect',
  'sum.collect.a': 'Your contact details, your date of birth, the health history you gave us on the PAR-Q, and everything your training produces — sessions, check-ins, measurements, photos you upload, and messages with your trainer.',
  'sum.whoSees.q': 'Who can see it',
  'sum.whoSees.a': 'Your trainer. Anyone you have named — a guardian or a support person. The companies that run the software for us, who may only use it to run it. Nobody buys it from us, because we do not sell it.',
  'sum.keep.q': 'How long we keep it',
  'sum.keep.a': 'For as long as you are a client, and then: invoices and payment records for 7 years, signed agreements and PAR-Qs for 7 years after the agreement ends, session notes for 5 years, messages for 3 years.',
  'sum.canDo.q': 'What you can do',
  'sum.canDo.a': 'Ask for a copy of everything, correct anything wrong, ask us to delete it, and change any of the choices below whenever you want. We answer within 45 days, and we never treat you differently for asking.',
  'sum.readPolicy': 'Read the full policy',
  'sum.sectionRef': 'Policy section {n}',
};

const ES = {
  'sum.title': 'En palabras sencillas',
  'sum.intro': 'Las cuatro cosas que la gente más nos pregunta, respondidas aquí y explicadas por completo en la política.',
  'sum.collect.q': 'Qué recopilamos',
  'sum.collect.a': 'Sus datos de contacto, su fecha de nacimiento, el historial de salud que nos dio en el PAR-Q y todo lo que produce su entrenamiento: sesiones, registros diarios, medidas, las fotos que suba y los mensajes con su entrenador.',
  'sum.whoSees.q': 'Quién puede verlo',
  'sum.whoSees.a': 'Su entrenador. Las personas que usted haya nombrado: un tutor o una persona de apoyo. Las empresas que operan el software por nosotros, que solo pueden usarlo para operarlo. Nadie nos lo compra, porque no lo vendemos.',
  'sum.keep.q': 'Cuánto tiempo lo conservamos',
  'sum.keep.a': 'Mientras usted sea cliente y, después: las facturas y los registros de pago 7 años, los acuerdos firmados y los PAR-Q 7 años después de que termine el acuerdo, las notas de sesión 5 años y los mensajes 3 años.',
  'sum.canDo.q': 'Qué puede hacer usted',
  'sum.canDo.a': 'Pedir una copia de todo, corregir lo que esté mal, pedirnos que lo eliminemos y cambiar cualquiera de las opciones de abajo cuando quiera. Respondemos en un plazo de 45 días y nunca lo tratamos distinto por pedirlo.',
  'sum.readPolicy': 'Leer la política completa',
  'sum.sectionRef': 'Sección {n} de la política',
};

export const PRIVACY_SUMMARY_PACKS = Object.freeze({ en: Object.freeze(EN), es: Object.freeze(ES) });

/** The pack for a language code. Anything that is not `es` is English. */
export function privacySummaryPack(lang) {
  return String(lang || '').slice(0, 2).toLowerCase() === 'es' ? ES : EN;
}

/**
 * The four rows, resolved for one language, in render order.
 * Returns [{ key, q, a, section, anchor }] — everything a caller needs to draw
 * a row and link it back at the policy, with no key strings at the callsite.
 */
export function privacySummaryRows(lang) {
  const pack = privacySummaryPack(lang);
  return PRIVACY_SUMMARY_ORDER.map((key) => {
    const s = PRIVACY_SUMMARY_SECTIONS[key];
    return {
      key,
      q: pack[`sum.${key}.q`],
      a: pack[`sum.${key}.a`],
      section: s.n,
      anchor: s.anchor,
      sectionLabel: String(pack['sum.sectionRef']).replace('{n}', String(s.n)),
    };
  });
}

if (typeof window !== 'undefined') {
  Object.assign(window, {
    PRIVACY_SUMMARY_PACKS,
    PRIVACY_SUMMARY_ORDER,
    PRIVACY_SUMMARY_SECTIONS,
    privacySummaryPack,
    privacySummaryRows,
  });
}
